import React from 'react';
import { Col, Row } from 'antd';

import { InfoCircleIcon } from '@/assets/icons/v2';

import { Arrow, Body, Divider, Header, InfoMark, Title, TitleLink } from './styles';

const FULL_SPAN = 24;
const ACTIVATION_KEYS = ['Enter', ' '];

interface AccordionProps {
	title: string;
	open: boolean;
	onToggle: () => void;
	children: JSX.Element;
	underline?: boolean;
	info?: boolean;
	extra?: JSX.Element;
	onTitleClick?: () => void;
}

interface TitleContentProps {
	title: string;
	underline: boolean;
	onTitleClick?: () => void;
}

function TitleContent({ title, underline, onTitleClick }: Readonly<TitleContentProps>): JSX.Element {
	if (!onTitleClick) {
		return <Title $underline={underline}>{title}</Title>;
	}

	function handleClick(event: React.MouseEvent<HTMLSpanElement>): void {
		event.stopPropagation();
		onTitleClick?.();
	}

	function handleKeyDown(event: React.KeyboardEvent<HTMLSpanElement>): void {
		if (!ACTIVATION_KEYS.includes(event.key)) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		onTitleClick?.();
	}

	return (
		<TitleLink $underline role="link" tabIndex={0} onClick={handleClick} onKeyDown={handleKeyDown}>
			{title}
		</TitleLink>
	);
}

export function Accordion({
	title,
	open,
	onToggle,
	children,
	underline = false,
	info = false,
	extra,
	onTitleClick
}: Readonly<AccordionProps>): JSX.Element {
	return (
		<Row>
			<Col span={FULL_SPAN}>
				<Header type="button" aria-expanded={open} onClick={onToggle}>
					<TitleContent title={title} underline={underline} onTitleClick={onTitleClick} />
					{info && (
						<InfoMark>
							<InfoCircleIcon />
						</InfoMark>
					)}
					<Row align="middle" justify="end" gutter={[40, 0]} style={{ flex: 1 }}>
						{!!extra && <Col>{extra}</Col>}
						<Col>
							<Arrow $open={open} />
						</Col>
					</Row>
				</Header>
			</Col>
			<Col span={FULL_SPAN}>
				<Divider />
			</Col>
			{open && (
				<Col span={FULL_SPAN}>
					<Body>{children}</Body>
				</Col>
			)}
		</Row>
	);
}
